import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Cloud, LogOut } from "lucide-react";
import { Button } from "../ui/button";
import { NAV_LINKS } from "../../mock";
import { useAuth } from "../../context/AuthContext";
import HelpButton from "@/components/HelpButton";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 group">
    <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm transition-transform group-hover:-translate-y-0.5">
      <Cloud className="h-5 w-5" strokeWidth={2.2} />
    </span>
    <span className="font-display text-xl font-bold tracking-tight text-foreground">
      Nimbus
    </span>
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAnchor = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-border bg-background/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Logo />

        <div className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => goToAnchor(l.href)}
              className="rounded-lg px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-2 md:flex">
          <HelpButton />
          {user ? (
            <div className="flex items-center gap-2">
              <img
                src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=059669&color=fff`}
                alt={user.name}
                className="h-8 w-8 rounded-full object-cover"
              />
              <span className="max-w-[120px] truncate text-sm font-medium text-foreground">{user.name}</span>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={logout}
                aria-label="Log out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => navigate("/login")}
            >
              Sign in
            </Button>
          )}
          <Button
            className="rounded-full bg-primary px-5 text-sm font-semibold shadow-sm transition-transform hover:-translate-y-0.5"
            onClick={() => navigate("/workspace")}
          >
            Try the demo
          </Button>
        </div>

        <div className="md:hidden">
          <HelpButton />
        </div>

        <button
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-foreground md:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-border bg-background/95 px-5 py-4 backdrop-blur-xl md:hidden">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => goToAnchor(l.href)}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                {l.label}
              </button>
            ))}
            <Button
              className="mt-3 w-full rounded-full bg-primary font-semibold"
              onClick={() => {
                setOpen(false);
                navigate("/workspace");
              }}
            >
              Try the demo
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
